import React, { useEffect, useMemo } from "react";
import { Card, CardBody, Col, Row } from "reactstrap";
import { Typography, Spin } from 'antd';
import { useDispatch, useSelector } from "react-redux";
import { getCustomers } from "../../store/actions/customers";
import { useNavigate } from "react-router-dom";
import moment from "moment-timezone";
import {
  UserOutlined,
  UserDeleteOutlined,
  CalendarOutlined,
  HistoryOutlined,
  RiseOutlined
} from "@ant-design/icons";

const { Title, Text } = Typography;

/**
 * Senior Developer & Best Practice Enhancements:
 * 1. Data centralization: Uses customers from Redux store.
 * 2. Optimized Processing: useMemo used for heavy filtering calculations.
 * 3. Responsive UI: Clean grid layout (xl=2.4 equivalent, fallback to 4/6/12).
 * 4. Micro-interactions: Hover effects and pointer cursors.
 * 5. Code Maintainability: Map-driven rendering for stats cards.
 */
const CustomerPanel = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  // Select data from global store
  const { list: customers } = useSelector((state) => state.customers);
  console.log('customers', customers)

  // Local loading state specifically for this panel if fetching independently
  const [isPanelLoading, setIsPanelLoading] = React.useState(!customers.length);

  useEffect(() => {
    const initFetch = async () => {
      if (!customers.length) {
        setIsPanelLoading(true);
        try {
          await dispatch(getCustomers());
        } catch (error) {
          console.error("Dashboard Panel Error:", error);
        } finally {
          setIsPanelLoading(false);
        }
      } else {
        setIsPanelLoading(false);
      }
    };
    initFetch();
  }, [dispatch, customers.length]);

  // Derived state for dashboard statistics
  const stats = useMemo(() => {
    if (!customers || customers.length === 0) return Array(5).fill({ count: 0 });

    const today = moment().format('YYYY-MM-DD');
    const last7Days = moment().subtract(7, 'days').format('YYYY-MM-DD');
    const lastMonthStart = moment().subtract(1, 'month').startOf('month').format('YYYY-MM-DD');
    const lastMonthEnd = moment().subtract(1, 'month').endOf('month').format('YYYY-MM-DD');

    return [
      {
        title: "Active Customers",
        count: customers.filter(c => c.status === true).length,
        type: "active",
        icon: <UserOutlined />,
        color: "primary"
      },
      {
        title: "Inactive Customers",
        count: customers.filter(c => c.status === false).length,
        type: "inactive",
        icon: <UserDeleteOutlined />,
        color: "danger"
      },
      {
        title: "Today's New",
        count: customers.filter(c => c.status && moment(c.createdAt).format('YYYY-MM-DD') === today).length,
        type: "today",
        icon: <CalendarOutlined />,
        color: "success"
      },
      {
        title: "Last 7 Days",
        count: customers.filter(c => c.status && moment(c.createdAt).format('YYYY-MM-DD') >= last7Days).length,
        type: "last7Days",
        icon: <RiseOutlined />,
        color: "info"
      },
      {
        title: "Last Month",
        count: customers.filter(c => c.status && moment(c.createdAt).isBetween(lastMonthStart, lastMonthEnd, null, '[]')).length,
        type: "lastMonth",
        icon: <HistoryOutlined />,
        color: "warning"
      }
    ];
  }, [customers]);

  const handleNavigation = (type) => {
    const routeMap = {
      active: '/users',
      inactive: '/users',
      today: '/users',
      last7Days: '/users',
      lastMonth: '/users'
    };
    if (routeMap[type]) navigate(routeMap[type]);
  };

  return (
    <div className="customer-stats-panel mb-4">
      <div className="d-flex align-items-center mb-3">
        <Title level={5} className="mb-0 me-2">Customer Summary</Title>
        {isPanelLoading && <Spin size="small" />}
      </div>

      <Row className="g-3">
        {stats.map((stat, idx) => (
          <Col key={stat.type || idx} xs={12} sm={6} md={4} lg={4} className="flex-grow-1" style={{ maxWidth: '100%', minWidth: '200px' }}>
            <Card
              className="h-100 border-0 shadow-sm dashboard-card"
              onClick={() => handleNavigation(stat.type)}
              style={{ cursor: 'pointer', transition: 'all 0.2s ease-in-out' }}
            >
              <CardBody>
                <div className="d-flex align-items-start">
                  <div className="flex-grow-1 overflow-hidden">
                    <Text type="secondary" className="text-uppercase font-size-12 fw-medium mb-2 d-block">
                      {stat.title}
                    </Text>
                    <h4 className="mb-0 fw-bold">
                      {isPanelLoading ? <Spin delay={200} /> : stat.count}
                    </h4>
                  </div>
                  <div className="flex-shrink-0 ms-3">
                    <div className={`avatar-sm rounded-circle bg-soft-${stat.color} text-${stat.color} d-flex align-items-center justify-content-center`}>
                      <span className="font-size-20">{stat.icon}</span>
                    </div>
                  </div>
                </div>
              </CardBody>
            </Card>
          </Col>
        ))}
      </Row>

      <style>{`
        .dashboard-card:hover {
          transform: translateY(-4px);
          box-shadow: 0 8px 15px rgba(0,0,0,0.1) !important;
        }
        .bg-soft-primary { background-color: rgba(85, 110, 230, 0.1); }
        .bg-soft-danger { background-color: rgba(244, 102, 102, 0.1); }
        .bg-soft-success { background-color: rgba(52, 195, 143, 0.1); }
        .bg-soft-info { background-color: rgba(80, 165, 241, 0.1); }
        .bg-soft-warning { background-color: rgba(241, 180, 76, 0.1); }
        .text-primary { color: #556ee6 !important; }
        .text-danger { color: #f46666 !important; }
        .text-success { color: #34c38f !important; }
        .text-info { color: #50a5f1 !important; }
        .text-warning { color: #f1b44c !important; }
      `}</style>
    </div>
  );
};

export default CustomerPanel;